import * as React from "react";
import { Suspense, useRef } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import logo from "assets/icons/suno-logo-white.svg";
import logoMark from "assets/icons/suno-mark-white.svg";
import ErrorBoundary from "components/error-boundary";
import SupaDemoButton from "components/supa-demo-button";
import { SUPADEMO_GUIDED_TOUR_ID } from "constants/supa-demo";
import { lazyWithRetry } from "utils/lazy-with-retry.util";

import { useSideMenuFlyout } from "./side-menu-flyout.component";
import { useSideMenuExpandHint } from "./use-side-menu-expand-hint.hook";

type SideMenuHeaderProps = {
  collapsed: boolean;
  /** On a phone the menu is a drawer: the toggle closes it instead of collapsing. */
  isMobile: boolean;
  onToggle: () => void;
  /** The guided tour (Assist Me) belongs to the main app's menu only. */
  showTour: boolean;
  /** Hidden while the quick launcher flag is off (the launcher renders nothing then). */
  showSearch: boolean;
  onSearch: () => void;
};

/**
 * Shown once per person, so it is never in the main bundle. No reload rung: a
 * hint that fails to load is not worth reloading anyone's page over.
 */
const SideMenuExpandHint = lazyWithRetry(
  () => import("./side-menu-expand-hint.component"),
  "side-menu-expand-hint",
  { reloadFallback: false }
);

function Nothing() {
  return null;
}

const focusRingSx = (color: string) => ({
  "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: "2px" }
});

/** A 60×32 rail button in the Search box's look: always visible, never a hidden trick. */
const railButtonSx = (white: string) => ({
  flex: "0 0 auto",
  height: "32px",
  minWidth: 0,
  mx: 0.75,
  mb: 0.75,
  borderRadius: "8px",
  border: `1px solid ${alpha(white, 0.14)}`,
  backgroundColor: alpha(white, 0.08),
  "&:hover": { backgroundColor: alpha(white, 0.14), color: white }
});

/**
 * The top of the icon rail (ENG-2535 follow-up): the logo mark, then a plainly
 * visible » button that brings the names back, in the same top corner as the «
 * that collapsed the menu, then the tour and search. Every button is named by
 * the rail's hover card. The first time someone is in the rail, a one-time card
 * beside » explains it (`SideMenuExpandHint`); expanding counts as having read it.
 */
function SideMenuRailHeader({
  onExpand,
  showTour,
  showSearch,
  onSearch
}: Pick<SideMenuHeaderProps, "showTour" | "showSearch" | "onSearch"> & {
  onExpand: () => void;
}) {
  const theme = useTheme();
  const flyout = useSideMenuFlyout();
  const expandRef = useRef<HTMLButtonElement>(null);
  const hint = useSideMenuExpandHint();
  const white = theme.palette.common.white;
  const focusRing = focusRingSx(theme.palette.sideMenu.itemBgcolorSelected);

  const railCard = (label: string, description: string) =>
    flyout
      ? {
          onMouseEnter: (event: React.MouseEvent<HTMLElement>) =>
            flyout.show(event.currentTarget, { label, description }),
          onFocus: (event: React.FocusEvent<HTMLElement>) =>
            flyout.show(event.currentTarget, { label, description }),
          onMouseLeave: () => flyout.hide(),
          onBlur: () => flyout.hide()
        }
      : {};

  return (
    <>
      <Box
        sx={{
          flex: "0 0 auto",
          display: "flex",
          justifyContent: "center",
          pt: 1.5,
          pb: 1
        }}
      >
        <Box
          component="img"
          src={logoMark}
          alt="Suno"
          sx={{ display: "block", height: "28px" }}
        />
      </Box>

      <Button
        ref={expandRef}
        aria-label="Expand sidebar"
        aria-expanded={false}
        onClick={() => {
          flyout?.hideNow();
          if (hint.visible) hint.dismiss({ source: "expanded" });
          onExpand();
        }}
        {...railCard("Expand menu", "Show the page names next to the icons.")}
        sx={{ ...railButtonSx(white), color: white, ...focusRing }}
      >
        <KeyboardDoubleArrowRightIcon sx={{ fontSize: 22 }} />
      </Button>
      {hint.visible && (
        <ErrorBoundary FallbackComponent={<Nothing />}>
          <Suspense fallback={null}>
            <SideMenuExpandHint
              anchorRef={expandRef}
              onDismiss={() => hint.dismiss({ source: "got_it" })}
            />
          </Suspense>
        </ErrorBoundary>
      )}

      {showTour && (
        <Box
          {...railCard("Assist Me", "A guided tour of Suno, step by step.")}
          sx={{ display: "flex", mx: 0.75, mb: 0.75 }}
        >
          <SupaDemoButton
            isSideMenu
            supaDemoId={SUPADEMO_GUIDED_TOUR_ID}
            label="guided-tour"
            sx={{
              flex: 1,
              height: "32px",
              minWidth: 0,
              justifyContent: "center",
              "& .MuiButton-startIcon": { m: 0 },
              "& .MuiTypography-root": { display: "none" },
              ...focusRing
            }}
          />
        </Box>
      )}

      {showSearch && (
        <Button
          aria-label="Search or jump to a page, Command K"
          onClick={() => {
            flyout?.hideNow();
            onSearch();
          }}
          {...railCard("Search  ⌘K", "Find a patient or jump to any page.")}
          sx={{
            ...railButtonSx(white),
            color: theme.palette.neutral[500],
            ...focusRing
          }}
        >
          <SearchIcon sx={{ fontSize: 20 }} />
        </Button>
      )}
    </>
  );
}

/**
 * The top of the side menu: logo + collapse toggle, the guided tour, and the
 * ⌘K search (ENG-2535). Collapsed, it is `SideMenuRailHeader`.
 */
export default function SideMenuHeader({
  collapsed,
  isMobile,
  onToggle,
  showTour,
  showSearch,
  onSearch
}: SideMenuHeaderProps) {
  const theme = useTheme();
  const focusRing = focusRingSx(theme.palette.sideMenu.itemBgcolorSelected);
  const toggleLabel = isMobile ? "Close menu" : "Collapse sidebar";

  if (collapsed && !isMobile) {
    return (
      <SideMenuRailHeader
        onExpand={onToggle}
        showTour={showTour}
        showSearch={showSearch}
        onSearch={onSearch}
      />
    );
  }

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          flex: "0 0 auto",
          pt: 1.5,
          pb: 1,
          pl: 1.75,
          pr: 0.75
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Suno"
          sx={{ display: "block", height: "24px" }}
        />
        <IconButton
          size="small"
          aria-label={toggleLabel}
          aria-expanded={isMobile ? undefined : true}
          title={toggleLabel}
          onClick={onToggle}
          sx={{
            width: "28px",
            height: "28px",
            borderRadius: "6px",
            color: theme.palette.neutral[500],
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.1),
              color: theme.palette.common.white
            },
            ...focusRing
          }}
        >
          {isMobile ? (
            <ChevronLeftIcon fontSize="small" />
          ) : (
            <KeyboardDoubleArrowLeftIcon fontSize="small" />
          )}
        </IconButton>
      </Stack>

      {showTour && (
        <SupaDemoButton
          isSideMenu
          supaDemoId={SUPADEMO_GUIDED_TOUR_ID}
          tooltip="Guided Tour"
          label="guided-tour"
          sx={{
            mx: 0.75,
            mb: 0.75,
            height: "32px",
            minWidth: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            justifyContent: "flex-start",
            ...focusRing
          }}
        />
      )}

      {showSearch && (
        <Button
          aria-label="Search or jump to a page, Command K"
          title="Search (⌘K)"
          onClick={onSearch}
          sx={{
            flex: "0 0 auto",
            height: "32px",
            minWidth: 0,
            mx: 0.75,
            mb: 0.75,
            pl: 1,
            pr: 0.75,
            gap: 0.75,
            justifyContent: "space-between",
            textTransform: "none",
            borderRadius: "8px",
            border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
            backgroundColor: alpha(theme.palette.common.white, 0.08),
            color: theme.palette.neutral[500],
            "&:hover": {
              backgroundColor: alpha(theme.palette.common.white, 0.14),
              color: theme.palette.common.white
            },
            ...focusRing
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          <Typography
            component="span"
            noWrap
            sx={{
              flex: "1 1 auto",
              minWidth: 0,
              textAlign: "left",
              fontSize: "14px"
            }}
          >
            Search
          </Typography>
          <Box
            component="kbd"
            sx={{
              font: "inherit",
              fontSize: "12px",
              lineHeight: "16px",
              px: 0.5,
              border: `1px solid ${alpha(theme.palette.common.white, 0.22)}`,
              borderRadius: "4px"
            }}
          >
            ⌘K
          </Box>
        </Button>
      )}
    </>
  );
}
